// useSignIns — the app's « se connecter avec… » secrets and their status, for one environment. Same
// plain useState/useEffect + `cancelled` convention as useAppSecrets. Refetches when the callback page
// announces a finished sign-in (sign_in_view SIGN_IN_CHANNEL), so the panel the owner left open shows
// « Connected » without a click.

import { useCallback, useEffect, useState } from "react";
import { disconnectSignIn, listSignIns, startSignIn, type SecretsEnv, type SignInStatus } from "../api";
import { SIGN_IN_CHANNEL } from "../lib/sign_in_view";

export type UseSignIns = {
  byName: Map<string, SignInStatus>;
  reload: () => void;
  /** Opens the service's sign-in window. Rejects with the server's reason (a value not set yet). */
  connect: (name: string) => Promise<void>;
  disconnect: (name: string) => Promise<void>;
};

export function useSignIns(slug: string, env: SecretsEnv): UseSignIns {
  const [byName, setByName] = useState<Map<string, SignInStatus>>(new Map());
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // A failure leaves the map empty: the secrets themselves still list, and the panel's error is theirs.
    listSignIns(slug, env)
      .then((r) => { if (!cancelled) setByName(new Map((r.grants ?? []).map((g) => [g.name, g]))); })
      .catch(() => { if (!cancelled) setByName(new Map()); });
    return () => { cancelled = true; };
  }, [slug, env, nonce]);

  useEffect(() => {
    let c: BroadcastChannel | null = null;
    try { c = new BroadcastChannel(SIGN_IN_CHANNEL); } catch { return; }
    c.onmessage = (e) => { if (e.data?.slug === slug) setNonce((n) => n + 1); };
    return () => c?.close();
  }, [slug]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const connect = useCallback(async (name: string) => {
    // The window opens NOW, inside the click: a window opened after an await is a popup the browser
    // blocks. It is pointed at the service once the platform has answered.
    const w = window.open("", "_blank", "popup,width=520,height=720");
    try {
      const r = await startSignIn(slug, name, env);
      if (!r.ok || !r.url) throw new Error(r.error ?? "the sign-in window could not be opened");
      if (!w) { window.location.assign(r.url); return; }     // popups blocked: the same page goes
      w.opener = null;                                       // the service's page gets no handle on the console
      w.location.href = r.url;
    } catch (e) { w?.close(); throw e; }
  }, [slug, env]);
  const disconnect = useCallback(async (name: string) => {
    const r = await disconnectSignIn(slug, name, env);
    if (!r.ok) throw new Error(r.error ?? "could not disconnect");
    setNonce((n) => n + 1);
  }, [slug, env]);

  return { byName, reload, connect, disconnect };
}
